export type OverrideGovernanceCategory = 'override' | 'wrapper';
export type OverrideGovernanceStatus = 'required' | 'suspect' | 'retired';
export type OverrideGovernancePlannedAction = 'keep' | 'edit' | 'remove-pending-confirmation' | 'removed';
export type OverrideGovernanceRisk = 'low' | 'medium' | 'high';

export type OverrideGovernanceEntry = {
  id: string;
  label: string;
  category: OverrideGovernanceCategory;
  status: OverrideGovernanceStatus;
  plannedAction: OverrideGovernancePlannedAction;
  risk: OverrideGovernanceRisk;
  justification: string;
  locations: string[];
  contractDependencies: string[];
};

export const MINIMUM_OVERRIDE_MODE_ENV_FLAG = 'VITE_YSTNJ_MINIMUM_OVERRIDE_MODE';

export const TRIEDGE_WRAPPER_LABELS = {
  shellRoot: 'TRIEDGE_WRAPPER_SHELL_ROOT_V1',
  externalRoot: 'TRIEDGE_WRAPPER_EXTERNAL_ROOT_V1',
  externalContentSlot: 'TRIEDGE_WRAPPER_EXTERNAL_CONTENT_SLOT_V1',
  parentFinancialAidSurface: 'TRIEDGE_WRAPPER_PARENT_FINANCIAL_AID_SURFACE_V1',
} as const;

export const TRIEDGE_OVERRIDE_LABELS = {
  forceAssistantCollapsed: 'TRIEDGE_OVERRIDE_FORCE_ASSISTANT_COLLAPSED_BY_DEFAULT_V1',
  stripCrossHeader: 'TRIEDGE_OVERRIDE_STRIP_EXTERNAL_CROSS_HEADER_V1',
  bridgedIndexRoute: 'TRIEDGE_OVERRIDE_BRIDGED_INDEX_ROUTE_V1',
  parentFinancialAidShellOnly: 'TRIEDGE_OVERRIDE_PARENT_FINANCIAL_AID_SHELL_ONLY_V1',
  parentFinancialAidHeaderPolicy: 'TRIEDGE_OVERRIDE_PARENT_FINANCIAL_AID_HEADER_POLICY_V1',
  parentFinancialAidContentInset: 'TRIEDGE_OVERRIDE_PARENT_FINANCIAL_AID_CONTENT_INSET_V1',
  defaultHeaderPolicy: 'TRIEDGE_OVERRIDE_HEADER_POLICY_DEFAULT_V1',
  defaultContentInset: 'TRIEDGE_OVERRIDE_CONTENT_INSET_DEFAULT_V1',
} as const;

export const OVERRIDE_GOVERNANCE_REGISTRY: Record<string, OverrideGovernanceEntry> = {
  override_force_assistant_collapsed_by_default: {
    id: 'override_force_assistant_collapsed_by_default',
    label: TRIEDGE_OVERRIDE_LABELS.forceAssistantCollapsed,
    category: 'override',
    status: 'retired',
    plannedAction: 'removed',
    risk: 'low',
    justification: 'Removed by policy: assistant visibility is now controlled explicitly by route/persona policy.',
    locations: ['apps/ystnj/src/app-shell/bridge-entry.tsx'],
    contractDependencies: ['testing/e2e/tests/ystnj/scenario-flows/ystnj-ai-assistant-active-routes-gate.spec.ts'],
  },
  override_strip_external_cross_header: {
    id: 'override_strip_external_cross_header',
    label: TRIEDGE_OVERRIDE_LABELS.stripCrossHeader,
    category: 'override',
    status: 'retired',
    plannedAction: 'removed',
    risk: 'low',
    justification: 'Removed by policy: global external cross-header stripping is no longer applied at runtime.',
    locations: [
      'apps/ystnj/src/app-shell/bridge-entry.tsx',
      'apps/ystnj/src/app-shell/bridge-entry.module.css',
    ],
    contractDependencies: ['testing/e2e/tests/ystnj/scenario-flows/portal-shell-reference-visual.spec.ts'],
  },
  wrapper_shell_mount_root: {
    id: 'wrapper_shell_mount_root',
    label: TRIEDGE_WRAPPER_LABELS.shellRoot,
    category: 'wrapper',
    status: 'suspect',
    plannedAction: 'remove-pending-confirmation',
    risk: 'medium',
    justification: 'Shell mount wrapper may be collapsible depending on ownership transfer completeness.',
    locations: ['apps/ystnj/src/app-shell/bridge-entry.tsx', 'apps/ystnj/src/components/app-shell/ParentAppShell.tsx'],
    contractDependencies: ['testing/e2e/tests/ystnj/ystnj-persona-route-sidebar-gate.spec.ts'],
  },
  wrapper_external_root_host: {
    id: 'wrapper_external_root_host',
    label: TRIEDGE_WRAPPER_LABELS.externalRoot,
    category: 'wrapper',
    status: 'suspect',
    plannedAction: 'remove-pending-confirmation',
    risk: 'medium',
    justification: 'External DOM host wrapper may become unnecessary after full compile-time ownership transfer.',
    locations: ['apps/ystnj/src/app-shell/bridge-entry.tsx'],
    contractDependencies: ['testing/e2e/tests/ystnj/submodule-routing-raw-bridged-exploration.spec.ts'],
  },
  wrapper_external_content_slot: {
    id: 'wrapper_external_content_slot',
    label: TRIEDGE_WRAPPER_LABELS.externalContentSlot,
    category: 'wrapper',
    status: 'suspect',
    plannedAction: 'remove-pending-confirmation',
    risk: 'medium',
    justification: 'External content slot wrapper is a bridge artifact and a candidate for removal after recomposition.',
    locations: ['apps/ystnj/src/app-shell/bridge-entry.tsx'],
    contractDependencies: ['testing/e2e/tests/ystnj/portal-family-bridge-global.spec.ts'],
  },
  wrapper_parent_financial_aid_surface: {
    id: 'wrapper_parent_financial_aid_surface',
    label: TRIEDGE_WRAPPER_LABELS.parentFinancialAidSurface,
    category: 'wrapper',
    status: 'suspect',
    plannedAction: 'remove-pending-confirmation',
    risk: 'low',
    justification: 'Parent financial-aid surface wrapper should remain minimal and removable if ownership boundary collapses.',
    locations: ['apps/ystnj/src/parent-portal/pages/PortalFinancialAidPage.tsx'],
    contractDependencies: ['testing/e2e/tests/ystnj/portal-family-ai-assistant-hidden.spec.ts'],
  },
};

export const OVERRIDE_GOVERNANCE_ENTRIES = Object.values(OVERRIDE_GOVERNANCE_REGISTRY);

export const SUSPECT_OVERRIDE_GOVERNANCE_ENTRIES = OVERRIDE_GOVERNANCE_ENTRIES
  .filter((entry) => entry.status === 'suspect');

export const isMinimumOverrideModeEnabled = (): boolean =>
  import.meta.env.VITE_YSTNJ_MINIMUM_OVERRIDE_MODE === '1';

type WindowWithOverrideGovernanceBootMarker = Window & {
  __triedgeMinimumOverrideModeBootLogEmitted?: boolean;
};

export const emitMinimumOverrideModeBootLog = (scope: string): void => {
  if (typeof window === 'undefined') return;
  const markerWindow = window as WindowWithOverrideGovernanceBootMarker;
  if (markerWindow.__triedgeMinimumOverrideModeBootLogEmitted) return;
  markerWindow.__triedgeMinimumOverrideModeBootLogEmitted = true;
  if (import.meta.env.PROD) return;
  const minimumOverrideModeValue = isMinimumOverrideModeEnabled() ? '1' : '0';
  console.info(
    `[AppShell][OverrideGovernance] ${MINIMUM_OVERRIDE_MODE_ENV_FLAG}=${minimumOverrideModeValue} (scope=${scope})`,
  );
};
